import type { ProviderImportProfile } from '@timelyapp/integration-cloud-api-spec';
import { Box, Message, Text } from '@timelyapp/react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  GetSubscribableResourcesApiResponse,
  UpdateBatchOfSubscriptionsApiArg,
} from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { ThunkDispatch } from '~types/redux';
import { useActiveImportProfile } from '../../hooks/useActiveImportProfile';
import {
  getEnrichedSubscribableResources,
  getPendingChangesState,
} from '../../redux/tic.integrations.selectors';
import { draftSubscriptionChange } from '../../redux/tic.integrations.slice';
import { IntegrationsSetupWizardStepSubscriptionManagerModal } from '../setup_wizard/Integrations.SetupWizard.Step.SubscriptionManager.Modal';
import { SubscriptionsList } from './Integrations.Shared.SubscriptionsList';

interface SubscriptionsManagerProps {
  /** Account ID for the subscription query */
  accountId: string;
  /** Connection ID for the subscription query */
  connectionId: string;
  /** Provider for the subscription query */
  provider: UpdateBatchOfSubscriptionsApiArg['provider'];
  /** Empty state message */
  emptyStateMessage?: string;
  importProfiles?: ProviderImportProfile[];
  activeProfileKey?: string | null;
}

/**
 * Self-contained subscription manager that handles data loading and Redux operations directly.
 */
export const SubscriptionsManager: React.FC<SubscriptionsManagerProps> = ({
  accountId,
  connectionId,
  provider,
  emptyStateMessage = 'No subscriptions available',
  importProfiles,
  activeProfileKey,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [editingSubscription, setEditingSubscription] = useState<
    GetSubscribableResourcesApiResponse['subscriptions'][0] | null
  >(null);

  const skip = !accountId || !connectionId || !provider;
  const activeProfile = useActiveImportProfile(importProfiles, activeProfileKey);

  // RTKQuery hook for data loading - this automatically calls setSubscriptionQueryArgs
  const { isLoading } = ticApi.useGetSubscribableResourcesQuery(
    { accountId, connectionId, provider },
    { skip, refetchOnMountOrArgChange: true }
  );

  // Redux selectors for enriched data and stats
  const subscriptionsData = useSelector(getEnrichedSubscribableResources);
  const pendingChangesState = useSelector(getPendingChangesState);

  const handleModalConfirm = (changes: Record<string, any>) => {
    // Apply individual changes
    Object.values(changes).forEach((change: any) => {
      dispatch(
        draftSubscriptionChange({
          subscription_id: change.subscription_id,
          resource_id: change.resource_id,
          enabled: change.enabled,
        })
      );
    });
    setEditingSubscription(null);
  };

  // Keep the list usable while refreshing. Only show a loader if we truly have no data yet.
  if (isLoading && subscriptionsData.length < 1) {
    return (
      <Box.Column gap="medium" style={{ padding: '1rem' }}>
        <div>Loading subscriptions...</div>
      </Box.Column>
    );
  }

  return (
    <Box.Column gap="medium" style={{ height: '100%' }}>
      {/* Keep the UI stable during background refetches. The wizard footer handles explicit reload messaging. */}
      <Box.Column gap="small" style={{ flex: 1, overflow: 'auto' }}>
        <SubscriptionsList
          subscriptions={subscriptionsData}
          onToggle={resource => {
            dispatch(draftSubscriptionChange({ ...resource, enabled: !resource.enabled }));
          }}
          onManageSubscriptions={setEditingSubscription}
          emptyStateMessage={emptyStateMessage}
          activeProfile={activeProfile}
        />
      </Box.Column>

      {/* Show pending changes info */}
      {pendingChangesState.hasChanges && (
        <Message variant="info" style={{ flexGrow: 0 }}>
          <Text.Label size="small">
            {pendingChangesState.count} subscription changes pending
          </Text.Label>
          <Text color="subtle" size="small">
            Changes will be applied when you proceed to the next step.
          </Text>
        </Message>
      )}

      {editingSubscription && (
        <IntegrationsSetupWizardStepSubscriptionManagerModal
          resource={editingSubscription as any}
          isOpen={!!editingSubscription}
          onClose={() => setEditingSubscription(null)}
          globalChanges={{}}
          onConfirm={handleModalConfirm}
          activeProfile={activeProfile}
        />
      )}
    </Box.Column>
  );
};
